function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, e) { if ("object" != typeof t || !t) return t; var r; if ("undefined" != typeof Symbol && void 0 !== (r = t[Symbol.toPrimitive])) { var i = r.call(t, e || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === e ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Auto-apply discount (session-wide)
// ----------------------------------------------------------------------------
//
// A page that auto-applies a discount (subscription-cards' "Auto-apply
// discount code" setting) remembers the code for the rest of the browser
// session, and every page keeps it on the cart from then on.
//
// Applying it once on page view is not enough: the Monk Commerce app pushes
// its own code list to the cart (Storefront API cartDiscountCodesUpdate) when
// it starts a session — the first page of a visit, and again on the first
// page after the cart is emptied — and that list is empty. Two layers:
// - guardDiscountCodeWrites() adds the remembered code to those requests, so
//   the code is never removed (if it were removed right before an add to cart,
//   the "Added to your cart" modal would be rendered at full price). The
//   theme's own code writes (cart-delivery-options) use keepAutoDiscountCodes().
// - After any cart write we re-check the cart and put the code back if it is
//   missing anyway (requests the guard can't read, other apps, etc.).
//
// ----------------------------------------------------------------------------

var STORAGE_KEY = 'sitka:auto-discount-codes';
var STOREFRONT_API_PATTERN = /\/api\/[^/]+\/graphql\.json/;
// Cart writes from the theme, Recharge, Monk (cart/*.js) and the Storefront
// API (graphql.json) all show up as resource timing entries.
var CART_WRITE_PATTERN = new RegExp("\\/cart\\/(add|change|update|clear)(\\.js)?(\\?|$)|".concat(STOREFRONT_API_PATTERN.source));
var RECHECK_DELAY_MS = 500;
// Our own update.js is a cart write too, so a code that never sticks (expired
// code, failing cart) would re-trigger forever. Cap the writes per page view.
var MAX_APPLIES_PER_PAGE = 3;
var recheckTimer = null;
var watching = false;
var appliesThisPage = 0;
var fetchGuarded = false;
var routesRoot = () => {
  var _window$Shopify;
  return ((_window$Shopify = window.Shopify) === null || _window$Shopify === void 0 || (_window$Shopify = _window$Shopify.routes) === null || _window$Shopify === void 0 ? void 0 : _window$Shopify.root) || '/';
};

/**
 * The cart API's `discount` param replaces the cart's whole code list, so the
 * new codes are merged into the ones already there. Returns null when there is
 * nothing new to apply.
 * @param {string[]} existingCodes  Codes already on the cart
 * @param {string[]} codesToApply  Codes to make sure are on the cart
 * @returns {string|null}
 */
export var mergeDiscountCodes = function mergeDiscountCodes() {
  var existingCodes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var codesToApply = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var merged = [...existingCodes];
  var known = new Set(existingCodes.map(code => code.toLowerCase()));
  codesToApply.forEach(code => {
    var trimmed = (code || '').trim();
    if (trimmed && !known.has(trimmed.toLowerCase())) {
      known.add(trimmed.toLowerCase());
      merged.push(trimmed);
    }
  });
  return merged.length > existingCodes.length ? merged.join(',') : null;
};
var readRememberedCodes = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || [];
  } catch (error) {
    return [];
  }
};
export var rememberAutoDiscount = function rememberAutoDiscount() {
  var codes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var cleanCodes = codes.map(code => (code || '').trim()).filter(Boolean);
  if (!cleanCodes.length) return;
  try {
    var remembered = readRememberedCodes();
    var merged = mergeDiscountCodes(remembered, cleanCodes);
    if (merged) {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged.split(',')));
    }
  } catch (error) {
    // Storage blocked (private mode, etc.): the page-view apply still runs.
  }
};

/**
 * Adds the codes remembered this session to a code list that is about to
 * replace the cart's codes. The theme's own writers (cart-delivery-options)
 * call this directly: their Storefront client keeps its own fetch reference,
 * so the fetch guard below never sees their requests.
 * @param {string[]} codes
 * @returns {string[]} `codes` itself when there is nothing to add
 */
export var keepAutoDiscountCodes = function keepAutoDiscountCodes() {
  var _window$Shopify2;
  var codes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  if ((_window$Shopify2 = window.Shopify) !== null && _window$Shopify2 !== void 0 && _window$Shopify2.designMode) return codes;
  var merged = mergeDiscountCodes(codes, readRememberedCodes());
  return merged ? merged.split(',') : codes;
};

/**
 * Returns `init` with the remembered codes merged into a Storefront API
 * cartDiscountCodesUpdate body, or `init` untouched for any other request.
 * @param {RequestInfo|URL} input
 * @param {RequestInit} [init]
 * @returns {RequestInit|undefined}
 */
export var withRememberedCodes = (input, init) => {
  try {
    var _payload$variables;
    var body = init === null || init === void 0 ? void 0 : init.body;
    if (typeof body !== 'string' || !body.includes('cartDiscountCodesUpdate')) return init;
    var url = typeof input === 'string' ? input : (input === null || input === void 0 ? void 0 : input.url) || (input === null || input === void 0 ? void 0 : input.href) || '';
    if (!STOREFRONT_API_PATTERN.test(url) || !readRememberedCodes().length) return init;
    var payload = JSON.parse(body);
    var codes = payload === null || payload === void 0 || (_payload$variables = payload.variables) === null || _payload$variables === void 0 ? void 0 : _payload$variables.discountCodes;
    if (!Array.isArray(codes)) return init;
    var keptCodes = keepAutoDiscountCodes(codes);
    if (keptCodes === codes) return init;
    return _objectSpread(_objectSpread({}, init), {}, {
      body: JSON.stringify(_objectSpread(_objectSpread({}, payload), {}, {
        variables: _objectSpread(_objectSpread({}, payload.variables), {}, {
          discountCodes: keptCodes
        })
      }))
    });
  } catch (error) {
    return init;
  }
};

/**
 * Wraps window.fetch once so cartDiscountCodesUpdate requests keep the
 * remembered codes. Every other request is forwarded with its original
 * arguments. Inert until a page remembers a code. Install as early as
 * possible (app.js module evaluation), before Monk's first sync.
 */
export var guardDiscountCodeWrites = () => {
  if (fetchGuarded || typeof window.fetch !== 'function') return;
  fetchGuarded = true;
  var originalFetch = window.fetch;
  window.fetch = function guardedFetch() {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    var guardedInit = withRememberedCodes(args[0], args[1]);
    if (guardedInit !== args[1]) args[1] = guardedInit;
    return originalFetch.apply(this, args);
  };
};

/**
 * Puts the given codes (default: the ones remembered this session) on the
 * cart if any are missing.
 * @param {string[]} [codes]
 */
export var ensureAutoDiscount = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator(function* () {
    var _window$Shopify3;
    var codes = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : readRememberedCodes();
    // Skip the theme editor so merchants' own carts don't pick up the code.
    if ((_window$Shopify3 = window.Shopify) !== null && _window$Shopify3 !== void 0 && _window$Shopify3.designMode) return;
    if (!codes.some(code => code && code.trim())) return;
    try {
      var currentCart = yield fetch("".concat(routesRoot(), "cart.js")).then(response => response.json());
      var existingCodes = (currentCart.discount_codes || []).map(discount => discount.code);
      var discount = mergeDiscountCodes(existingCodes, codes);
      if (!discount || appliesThisPage >= MAX_APPLIES_PER_PAGE) return;
      appliesThisPage += 1;

      // keepalive lets the update finish if the shopper navigates away
      // before it resolves.
      var response = yield fetch("".concat(routesRoot(), "cart/update.js"), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          discount
        }),
        keepalive: true
      });
      if (!response.ok) {
        appliesThisPage = MAX_APPLIES_PER_PAGE;
        throw new Error("cart/update.js responded ".concat(response.status));
      }
    } catch (error) {
      console.error('Could not apply the auto discount code.', error);
    }
  });
  return function ensureAutoDiscount() {
    return _ref.apply(this, arguments);
  };
}();
var scheduleRecheck = () => {
  clearTimeout(recheckTimer);
  recheckTimer = setTimeout(() => ensureAutoDiscount(), RECHECK_DELAY_MS);
};

/**
 * Starts re-checking the cart after every cart write. Safe to call more than
 * once; does nothing until a code has been remembered this session.
 */
export var watchAutoDiscount = () => {
  if (watching || !readRememberedCodes().length) return;
  if (!('PerformanceObserver' in window)) return;
  watching = true;
  try {
    new PerformanceObserver(list => {
      if (list.getEntries().some(entry => CART_WRITE_PATTERN.test(entry.name))) {
        scheduleRecheck();
      }
    }).observe({
      type: 'resource',
      buffered: true
    });
  } catch (error) {
    watching = false;
  }
};

/**
 * Global entry point (app.js): keep a remembered code on the cart on every
 * page of the session.
 */
export var initAutoDiscount = () => {
  if (!readRememberedCodes().length) return;
  ensureAutoDiscount();
  watchAutoDiscount();
};